import React from "react";
import { supabase } from "../../lib/supabaseClient";
import type { EnrichedParticipant } from "../../types/participants";

interface ParticipantCardProps {
  participant: EnrichedParticipant;
  expanded: boolean;
  onClick: () => void;
  viewerId: string;
  tripIsPublic: boolean;
  tripCreatorId: string;
  isViewerOrganizer: boolean;
  onToggleOrganizer?: (userId: string, isCoOrganizer: boolean) => void;
  onToggleBlock?: (userId: string, isBlocked: boolean) => void;
  isBlocked: boolean;
  actionButtons?: React.ReactNode;
  hideExpandIcon?: boolean;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  expanded,
  onClick,
  viewerId,
  tripIsPublic,
  tripCreatorId,
  onToggleOrganizer,
  onToggleBlock,
  isViewerOrganizer,
  isBlocked,
  actionButtons,
  hideExpandIcon,
}) => {
  const isSelf = viewerId === participant.user_id;

  const {
    first_name,
    last_name,
    email,
    profile_picture_url,
    allergies,
    emergency_contact_name,
    emergency_contact_phone,
    can_swim,
    diet,
    clothing_size,
    medications,
    notes,
    birthdate,
    gender,
    languages,
    has_insurance,
    licenses,
    notification_preference,
    is_co_organizer,
  } = participant;

  const [signedProfilePicUrl, setSignedProfilePicUrl] = React.useState<
    string | null
  >(null);

  const getEmailPrefix = (email?: string | null): string => {
    if (!email) return "Unbekannt";
    return email.split("@")[0];
  };

  const fullName =
    first_name || last_name
      ? `${first_name ?? ""} ${last_name ?? ""}`.trim()
      : getEmailPrefix(email);

  React.useEffect(() => {
    const fetchSignedUrl = async () => {
      if (profile_picture_url) {
        const { data, error } = await supabase.storage
          .from("profile-pictures")
          .createSignedUrl(profile_picture_url, 60 * 60);

        if (error) {
          console.error("Fehler beim Laden des Profilbilds:", error.message);
        } else {
          setSignedProfilePicUrl(data?.signedUrl ?? null);
        }
      }
    };

    fetchSignedUrl();
  }, [profile_picture_url]);

  /** Compact label + value pair for the expanded detail grid */
  const InfoRow = ({
    label,
    value,
    bool,
    wide,
  }: {
    label: string;
    value: string;
    bool?: boolean;
    wide?: boolean;
  }) => (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] text-slate-600 uppercase tracking-wider mb-0.5">
        {label}
      </div>
      {bool != null ? (
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
            bool
              ? "bg-emerald-500/15 text-emerald-400"
              : "bg-[#1e2639] text-slate-500"
          }`}
        >
          {value}
        </span>
      ) : (
        <div className="text-xs text-slate-300 leading-snug">{value}</div>
      )}
    </div>
  );

  return (
    <div
      onClick={onClick}
      className="bg-[#161b27] rounded-xl border border-[#2a3347] mb-2 cursor-pointer hover:border-sky-500/20 transition-colors overflow-hidden"
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-[#2a3347] overflow-hidden shrink-0 flex items-center justify-center">
          {signedProfilePicUrl ? (
            <img
              src={signedProfilePicUrl}
              alt={fullName}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xs font-medium text-slate-400">
              {fullName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Name + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-slate-100 truncate">
              {fullName}
            </span>
            {participant.user_id === tripCreatorId && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/25 shrink-0">
                Organisator
              </span>
            )}
            {is_co_organizer && participant.user_id !== tripCreatorId && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/25 shrink-0">
                Co-Organisator
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-600 truncate">
            {getEmailPrefix(email)}
          </div>
        </div>

        {/* Toggle arrow */}
        {!hideExpandIcon && (
          <span className="text-slate-600 text-[10px] shrink-0">
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>

      {/* Expanded detail grid */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#1e2639]">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3">
            {allergies && <InfoRow label="Allergien" value={allergies} />}
            {diet && <InfoRow label="Ernährung" value={diet} />}
            {clothing_size && (
              <InfoRow label="Kleidergröße" value={clothing_size} />
            )}
            {can_swim != null && (
              <InfoRow
                label="Schwimmen"
                value={can_swim ? "Ja" : "Nein"}
                bool={can_swim}
              />
            )}
            {has_insurance != null && (
              <InfoRow
                label="Reiseversicherung"
                value={has_insurance ? "Ja" : "Nein"}
                bool={has_insurance}
              />
            )}
            {birthdate && (
              <InfoRow
                label="Geburtsdatum"
                value={new Date(birthdate).toLocaleDateString()}
              />
            )}
            {gender && <InfoRow label="Geschlecht" value={gender} />}
            {emergency_contact_name && (
              <InfoRow
                label="Notfallkontakt"
                value={emergency_contact_name}
              />
            )}
            {emergency_contact_phone && (
              <InfoRow
                label="Notfall-Tel."
                value={emergency_contact_phone}
              />
            )}
            {languages && languages.length > 0 && (
              <InfoRow label="Sprachen" value={languages.join(", ")} />
            )}
            {licenses && licenses.length > 0 && (
              <InfoRow label="Führerscheine" value={licenses.join(", ")} />
            )}
            {notification_preference &&
              notification_preference.length > 0 && (
                <InfoRow
                  label="Benachrichtigungen"
                  value={notification_preference.join(", ")}
                />
              )}
            {medications && (
              <InfoRow label="Medikamente" value={medications} wide />
            )}
            {notes && (
              <InfoRow label="Wichtige Hinweise" value={notes} wide />
            )}
          </div>

          {/* Organizer actions */}
          {isViewerOrganizer &&
            participant.user_id !== viewerId &&
            !actionButtons && (
              <div className="flex gap-2 flex-wrap mt-4 pt-3 border-t border-[#1e2639]">
                {onToggleOrganizer && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleOrganizer(
                        participant.user_id,
                        !participant.is_co_organizer
                      );
                    }}
                    className={`text-xs px-3 py-1 rounded-full transition-colors ${
                      participant.is_co_organizer
                        ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                    }`}
                  >
                    {participant.is_co_organizer
                      ? "Co-Organisator entfernen"
                      : "Zu Co-Organisator machen"}
                  </button>
                )}
                {onToggleBlock && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleBlock(participant.user_id, !isBlocked);
                    }}
                    className={`text-xs px-3 py-1 rounded-full transition-colors ${
                      isBlocked
                        ? "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20"
                        : "bg-[#1e2639] text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {isBlocked ? "Blockierung aufheben" : "Teilnehmer blockieren"}
                  </button>
                )}
              </div>
            )}
        </div>
      )}

      {actionButtons && (
        <div className="px-4 pb-3 flex gap-2 justify-end border-t border-[#1e2639]">
          {actionButtons}
        </div>
      )}
    </div>
  );
};

export default ParticipantCard;
